<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, useTemplateRef } from "vue"
import { useI18n } from "vue-i18n"
import { useEventBus } from "@vueuse/core"
import { useAuthStore } from "@core/stores/auth.store"
import { ComparateurFacturesService } from "@modules/courtage/services/comparateur-factures.service"
import EnergyContractGazForm from "./EnergyContractGazForm.vue"
import type {
    EnergyContract,
    EnergyContractWritePayload,
    NotificationEvent,
} from "@types"

// ================================
// PROPS / EMITS / MODEL
// ================================
type Props = {
    /** Sources (compteurs gaz GRDF/Regaz) couvertes par le contrat. */
    idSources: string[]
    /** Company détentrice ; à défaut, déduite du store auth. */
    idCompany?: string
    /** Contrat existant pour l'édition (null = création). */
    contract?: EnergyContract | null
    visible?: boolean
}
const props = withDefaults(defineProps<Props>(), {
    idCompany: undefined,
    contract: null,
    visible: false,
})

const [modelVisible] = defineModel<boolean>("visible", {
    required: true,
    default: false,
})

const emit = defineEmits<{
    (e: "saved", contract: EnergyContract): void
}>()

// ================================
// I18N
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title_create: "Saisir un contrat gaz",
            title_edit: "Modifier le contrat gaz",
            cancel: "Annuler",
            validate: "Valider",
            saved_ok: "Contrat enregistré.",
            saved_ko: "Échec de l'enregistrement du contrat.",
        },
    },
})

// ================================
// STATE
// ================================
const authStore = useAuthStore()
const service = new ComparateurFacturesService()
const formRef = useTemplateRef("formRef")
const loading = ref(false)
const errorMessage = ref("")
const currentPayload = ref<EnergyContractWritePayload | null>(null)
const busNotification = useEventBus<NotificationEvent>("notification_toast")

const isEdit = computed(() => Boolean(props.contract?._id))
const resolvedCompanyId = computed(
    () =>
        props.idCompany ??
        authStore.companyId ??
        authStore.companyIdcompany ??
        ""
)

// ================================
// HANDLERS
// ================================
function onUpdatePayload(payload: EnergyContractWritePayload) {
    currentPayload.value = payload
}

async function onSubmit() {
    if (!formRef.value?.valid || !currentPayload.value) return
    loading.value = true
    errorMessage.value = ""
    try {
        const payload: EnergyContractWritePayload = {
            ...currentPayload.value,
            _id_company: resolvedCompanyId.value,
            _id_sources: props.idSources,
        }
        const saved =
            isEdit.value && props.contract
                ? await service.updateEnergyContract(
                      props.contract._id,
                      payload
                  )
                : await service.createEnergyContract(payload)

        busNotification.emit({
            severity: "success",
            title: t("saved_ok"),
            life: 3000,
        })
        emit("saved", saved)
        modelVisible.value = false
    } catch (err) {
        const error = err as Error
        errorMessage.value = error?.message || t("saved_ko")
    } finally {
        loading.value = false
    }
}
</script>

<template>
    <BaseDialog
        v-model:visible="modelVisible"
        modal
        :closable="true"
        dismissable-mask
        class="modal-energy-contract-gaz max-w-[52rem] w-full"
        :pt="{
            header: 'flex-col-reverse p-0!',
            headeractions: 'ml-auto',
            title: 'text-primary mb-4',
        }"
        :header="isEdit ? t('title_edit') : t('title_create')"
    >
        <EnergyContractGazForm
            ref="formRef"
            :id-company="resolvedCompanyId"
            :id-sources="idSources"
            :model-value="contract"
            @update:payload="onUpdatePayload"
        />

        <BaseMessage v-if="errorMessage" severity="error" class="mt-4">
            {{ errorMessage }}
        </BaseMessage>

        <template #footer>
            <div class="flex w-full justify-between">
                <BaseButton
                    :label="t('cancel')"
                    text
                    severity="secondary"
                    @click="modelVisible = false"
                />
                <BaseButton
                    :label="t('validate')"
                    severity="primary"
                    :loading="loading"
                    :disabled="!formRef?.valid"
                    @click="onSubmit"
                />
            </div>
        </template>
    </BaseDialog>
</template>

<style scoped></style>
